/**
 * FILO-78 — Finestra «Imposta password» della scheda Utenti: l'amministratore assegna una password
 * a un altro utente e decide se obbligarlo a cambiarla al prossimo accesso (spuntato di partenza).
 * La password non viene inviata a nessuno: la nota lo ricorda. Gli errori del server (password fuori
 * dalla politica dell'istanza, già tradotti dal backend) restano nella finestra, così
 * l'amministratore corregge senza riscrivere tutto.
 */
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import { extractError } from '../../store/thunkHelpers';
import { userApi, type Member } from '../../entities/user/userApi';
import { useToast } from '../useToast';

const SET_PASSWORD_FORM_ID = 'set-user-password-form';

export function SetUserPasswordDialog(
  { member, onClose }: { member: Member; onClose: () => void },
) {
  const { t } = useTranslation();
  const toast = useToast();
  const [password1, setPassword1] = useState('');
  const [password2, setPassword2] = useState('');
  const [passwordReset, setPasswordReset] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // La conferma si segnala solo quando è stata scritta: un campo vuoto non è ancora un errore.
  const mismatch = password2.length > 0 && password1 !== password2;
  const canSubmit = password1.length > 0 && password1 === password2 && !saving;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      await userApi.setUserPassword(member.userId, { password1, password2, passwordReset });
      toast({ severity: 'success', message: t('userSettings.usersTeam.setPassword.saved') });
      onClose();
    } catch (err) {
      setError(extractError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth className="app-dialog">
      <DialogTitle className="app-dialog__header">
        <span className="app-dialog__title">
          {t('userSettings.usersTeam.setPassword.title', {
            name: `${member.firstName} ${member.lastName}`.trim(),
          })}
        </span>
        <IconButton
          aria-label={t('common.cancel')}
          onClick={onClose}
          size="small"
          className="app-dialog__close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent className="app-dialog__body">
        <form id={SET_PASSWORD_FORM_ID} onSubmit={handleSubmit}>
          {error && <Alert severity="error" className="form-field">{error}</Alert>}
          <TextField
            type="password"
            label={t('userSettings.usersTeam.setPassword.password')}
            value={password1}
            onChange={(e) => setPassword1(e.target.value)}
            autoComplete="new-password"
            required
            fullWidth
            className="form-field"
          />
          <TextField
            type="password"
            label={t('userSettings.usersTeam.setPassword.confirm')}
            value={password2}
            onChange={(e) => setPassword2(e.target.value)}
            autoComplete="new-password"
            required
            fullWidth
            className="form-field"
            error={mismatch}
            helperText={mismatch ? t('userSettings.security.mismatch') : undefined}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={passwordReset}
                onChange={(e) => setPasswordReset(e.target.checked)}
              />
            }
            label={t('userSettings.usersTeam.setPassword.passwordReset')}
          />
          <p className="text-xs text-muted">{t('userSettings.usersTeam.setPassword.note')}</p>
        </form>
      </DialogContent>
      <DialogActions className="app-dialog__footer">
        <Button type="submit" form={SET_PASSWORD_FORM_ID} variant="contained" disabled={!canSubmit}>
          {t('common.save')}
        </Button>
        <Button type="button" onClick={onClose} variant="outlined">
          {t('common.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
